import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/pro-regular-svg-icons";

import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import LoadingTablePage from "../LoadingTablePage";
import toast from "react-hot-toast";

import ConfirmationModal from "../../components/ConfirmationModal";
import AccessGroupForm from "../../components/accessGroup/AccessGroupForm";
import EntityMultiSelectForm from "../../components/EntityMultiSelectForm";

import { useAuth, Roles } from "../../providers/AuthContext";
import { getAccessGroupDetailed, updateAccessGroup } from "../../api/AccessGroups";
import { getAllCustomersByOrganizationPublicId } from "../../api/Customers";
import {
    getCustomerDisplayName,
    shouldShowOrganizationName,
} from "../../helpers/CustomerDisplay";

import { getAllChargingSitesByOrganizationPublicId } from "../../api/ChargingSites";

const EditAccessGroupsPage = () => {
    const { t } = useTranslation();
    const { getAccessToken, hasRole } = useAuth();
    const location = useLocation();
    const { customerPublicId } = location.state || {};
    const { id } = useParams();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(true);
    const [hasChanges, setHasChanges] = useState(false);
    const [confirmState, setConfirmState] = useState({ isOpen: false, id: "" });

    const [originalAccessGroup, setOriginalAccessGroup] = useState(null);
    const [originalCustomerIds, setOriginalCustomerIds] = useState([]);
    const [originalSiteIds, setOriginalSiteIds] = useState([]);

    const [accessGroup, setAccessGroup] = useState({
        name: "",
        description: "",
        hasUniversalSiteAccess: false,
        accessGroupPublicId: "",
        organizationName: "",
        organizationPublicId: "",
    });

    const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
    const [selectedSiteIds, setSelectedSiteIds] = useState([]);
    const [universalConfirm, setUniversalConfirm] = useState({
        isOpen: false,
        pendingValue: false,
    });

    const handleInputChange = (key, value) => {
        if (key === "hasUniversalSiteAccess" && value === true && selectedSiteIds.length > 0) {
            setUniversalConfirm({ isOpen: true, pendingValue: true });
            return;
        }

        setAccessGroup((prev) => ({ ...prev, [key]: value }));
    };


    // --- Load access group ---
    useEffect(() => {
        handleGetAccessGroup();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handleGetAccessGroup = async () => {
        setLoading(true);
        try {
            const token = await getAccessToken();
            const ag = await getAccessGroupDetailed(id, token);
            const organizationPublicId =
              ag?.organizationPublicId ?? ag?.OrganizationPublicId ?? "";
            const organizationName =
              ag?.organizationName ?? ag?.OrganizationName ?? "";

            const normalized = {
                name: ag.name ?? "",
                description: ag.description ?? "",
                hasUniversalSiteAccess: !!ag.hasUniversalSiteAccess,
                accessGroupPublicId: ag.accessGroupPublicId,
                organizationName,
                organizationPublicId,
            };

            const customerIds = (ag.customers ?? [])
                .map((c) => c.customerPublicId)
                .filter(Boolean);

            const siteIds = (ag.sites ?? [])
                .map((s) => s.chargingSitePublicId)
                .filter(Boolean);

            setAccessGroup(normalized);
            setSelectedCustomerIds(customerIds);
            setSelectedSiteIds(siteIds);

            setOriginalAccessGroup(normalized);
            setOriginalCustomerIds(customerIds);
            setOriginalSiteIds(siteIds);
        } catch (e) {
            toast.error(e);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (accessGroup.hasUniversalSiteAccess && selectedSiteIds.length > 0) {
            setSelectedSiteIds([]);
        }
    }, [accessGroup.hasUniversalSiteAccess, selectedSiteIds]);

    // --- hasChanges (form + ids) ---
    const changeSignature = useMemo(() => {
        const form = {
            name: accessGroup.name ?? "",
            description: accessGroup.description ?? "",
            hasUniversalSiteAccess: !!accessGroup.hasUniversalSiteAccess,
            accessGroupPublicId: accessGroup.accessGroupPublicId,
            organizationName: accessGroup.organizationName ?? "",
            organizationPublicId: accessGroup.organizationPublicId ?? "",
        };

        const sort = (arr) => [...arr].sort();
        return JSON.stringify({
            form,
            customers: sort(selectedCustomerIds),
            sites: sort(selectedSiteIds),
        });
    }, [accessGroup, selectedCustomerIds, selectedSiteIds]);

    const originalSignature = useMemo(() => {
        if (!originalAccessGroup) return "";
        const sort = (arr) => [...arr].sort();
        return JSON.stringify({
            form: originalAccessGroup,
            customers: sort(originalCustomerIds),
            sites: sort(originalSiteIds),
        });
    }, [originalAccessGroup, originalCustomerIds, originalSiteIds]);

    useEffect(() => {
        if (!originalAccessGroup) return;
        setHasChanges(changeSignature !== originalSignature);
    }, [changeSignature, originalSignature, originalAccessGroup]);

    const handleUpdateAccessGroup = async () => {
        const toastId = toast.loading(t("toast.updating", { entity: t("entity.accessGroup") }));
        try {
            const token = await getAccessToken();

            const payload = {
                ...accessGroup,
                hasUniversalSiteAccess: !!accessGroup.hasUniversalSiteAccess,
                chargingSitePublicIds: accessGroup.hasUniversalSiteAccess ? [] : selectedSiteIds,
                customerPublicIds: selectedCustomerIds,
            };


            await updateAccessGroup(payload, token);

            // refresh
            const refreshed = await getAccessGroupDetailed(id, token);
            const refreshedOrganizationPublicId =
              refreshed?.organizationPublicId ?? refreshed?.OrganizationPublicId ?? "";
            const refreshedOrganizationName =
              refreshed?.organizationName ?? refreshed?.OrganizationName ?? "";

            const normalized = {
                name: refreshed.name ?? "",
                description: refreshed.description ?? "",
                hasUniversalSiteAccess: !!refreshed.hasUniversalSiteAccess,
                accessGroupPublicId: refreshed.accessGroupPublicId,
                organizationName: refreshedOrganizationName,
                organizationPublicId: refreshedOrganizationPublicId,
            };

            const customerIds = (refreshed.customers ?? [])
                .map((c) => c.customerPublicId)
                .filter(Boolean);

            const siteIds = (refreshed.sites ?? [])
                .map((s) => s.chargingSitePublicId)
                .filter(Boolean);

            setAccessGroup(normalized);
            setSelectedCustomerIds(customerIds);
            setSelectedSiteIds(siteIds);

            setOriginalAccessGroup(normalized);
            setOriginalCustomerIds(customerIds);
            setOriginalSiteIds(siteIds);

            toast.success(t("toast.updated", { entity: t("entity.accessGroup") }), { id: toastId });
            setHasChanges(false);
        } catch (error) {
            toast.error(error?.message, {
                id: toastId,
            });
        }
    };

    const handleBackClick = () => {
        const go = () => {
            if (customerPublicId) {
                navigate(`/customer/${customerPublicId}?tab=accessGroups`);
            } else if (window.history.length > 1) {
                navigate(-1);
            } else {
                navigate("/accessGroups");
            }
        };

        if (hasChanges) {
            setConfirmState((s) => ({
                ...s,
                isOpen: true,
                title: t("confirm.discard"),
                confirm: () => {
                    setConfirmState((p) => ({ ...p, isOpen: false }));
                    go();
                },
            }));
        } else {
            go();
        }
    };

    return (
        <>
            <HeaderBar />
            <div className="flex flex-row w-full">
                <Sidebar />
                <main className="page">
                    <div className="absolute inset-0 pointer-events-none">
                        <LoadingTablePage title={t("pages.editAccessGroup")} loading={loading} />
                    </div>

                    <div className={`${loading ? "loading" : ""} page-content`}>
                        <div className="flex flex-row items-center pb-4">
                            <button onClick={handleBackClick} className="text-black flex">
                                <FontAwesomeIcon icon={faArrowLeft} className="w-4 pr-4" />
                            </button>

                            <h2 className="border-l border-mute-200 pl-4 ">
                                {accessGroup.name || t("pages.editAccessGroup")}
                            </h2>

                            <div className="flex gap-4 ml-auto">
                                <button className="btn btn-outline-primary" onClick={handleBackClick}>
                                    {t("common.cancel")}
                                </button>

                                <button
                                    className="btn btn-primary"
                                    disabled={!hasChanges}
                                    onClick={handleUpdateAccessGroup}
                                >
                                    {t("common.save")}
                                </button>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 xl:grid-cols-2 gap-8">
                            {/* DETAILS – full width */}
                            <div className="xl:col-span-2">
                                <AccessGroupForm
                                    accessGroup={accessGroup}
                                    handleInputChange={handleInputChange}
                                    showGlobalAccess={hasRole(Roles.Admin)}
                                    showOrganizationName={false}
                                />
                            </div>

                            {/* CUSTOMERS */}
                            {!loading && (
                                <EntityMultiSelectForm
                                    title={t("entity.customers")}
                                    fetchItems={async (token) => {
                                        const list = await getAllCustomersByOrganizationPublicId(
                                            accessGroup.organizationPublicId || null,
                                            token
                                        );
                                        return (list || []).map((c) => ({
                                            ...c,
                                            customerPublicId: c.customerPublicId,
                                            customerName: getCustomerDisplayName(
                                                c,
                                                shouldShowOrganizationName(hasRole)
                                            ),
                                        }));
                                    }}
                                    labelKey="customerName"
                                    valueKey="customerPublicId"
                                    selectedIds={selectedCustomerIds}
                                    onChange={(ids) => setSelectedCustomerIds(ids)}
                                />
                            )}

                            {/* SITES */}
                            {accessGroup.hasUniversalSiteAccess !==true && (
                                !loading && (
                                    <EntityMultiSelectForm
                                        title={t("entity.sites")}
                                        fetchItems={async (token) => {
                                            const list = await getAllChargingSitesByOrganizationPublicId(
                                                accessGroup.organizationPublicId || null,
                                                token
                                            );
                                            return (list || []).map((s) => ({
                                                ...s,
                                                chargingSitePublicId: s.chargingSitePublicId,
                                                chargingSiteName: s.chargingSiteName || s.name || "",
                                            }));
                                        }}
                                        labelKey="chargingSiteName"
                                        valueKey="chargingSitePublicId"
                                        selectedIds={selectedSiteIds}
                                        onChange={(ids) => setSelectedSiteIds(ids)}
                                    />
                                )
                            )}
                        </div>

                    </div>
                </main>

                <ConfirmationModal
                    isOpen={confirmState.isOpen}
                    title={confirmState.title}
                    onClose={() => setConfirmState({ ...confirmState, isOpen: false })}
                    onConfirm={() => {
                        setConfirmState({ ...confirmState, isOpen: false });
                        confirmState.confirm();
                    }}
                />
                <ConfirmationModal
                    isOpen={universalConfirm.isOpen}
                    title={t("accessGroup.removeSites")}
                    onClose={() => setUniversalConfirm({ isOpen: false, pendingValue: false })}
                    onConfirm={() => {
                        setSelectedSiteIds([]);
                        setAccessGroup((prev) => ({ ...prev, hasUniversalSiteAccess: true }));
                        setUniversalConfirm({ isOpen: false, pendingValue: false });
                    }}
                />

            </div>
        </>
    );
};

export default EditAccessGroupsPage;
